/**
 * Recovery handler for `409 PROCESSOR_MISMATCH` responses from the Defera
 * Capital create-* edge functions.
 *
 * Default behavior is to auto-reload after a short toast (the freshly-mounted
 * `useDeferaCapitalProcessor` hook then renders the correct form). But if the
 * customer hits the mismatch a SECOND time within ~60s on the same trip, the
 * platform toggle and the frontend are clearly out of sync in a way a reload
 * won't fix — we stop auto-reloading and surface a manual recovery message so
 * we don't trap the customer in an infinite refresh loop.
 *
 * Why sessionStorage and not React state: the counter has to survive across
 * the page reload itself. Cleared automatically when the tab closes.
 */
const STORAGE_KEY_PREFIX = 'defera_capital_processor_mismatch:';
const WINDOW_MS = 60_000;

function readEntry(tripRef) {
  if (typeof sessionStorage === 'undefined' || !tripRef) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + tripRef);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.count !== 'number' || typeof parsed?.ts !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeEntry(tripRef, entry) {
  if (typeof sessionStorage === 'undefined' || !tripRef) return;
  try {
    sessionStorage.setItem(STORAGE_KEY_PREFIX + tripRef, JSON.stringify(entry));
  } catch {
    /* sessionStorage may be unavailable in private modes — ignore */
  }
}

/**
 * Record a PROCESSOR_MISMATCH event and decide whether to auto-reload.
 *
 * @param {string} tripRef - The trip reference for this checkout (use any
 *   stable id if you don't have a trip ref yet — e.g. 'pre_trip')
 * @returns {{ shouldAutoReload: boolean, count: number }}
 */
export function recordProcessorMismatch(tripRef) {
  const now = Date.now();
  const prev = readEntry(tripRef);
  const expired = !prev || now - prev.ts > WINDOW_MS;
  const count = expired ? 1 : prev.count + 1;
  writeEntry(tripRef, { count, ts: now });
  return { shouldAutoReload: count < 2, count };
}

/** Customer-facing messages keyed off whether we're auto-reloading. */
export function processorMismatchToast(shouldAutoReload) {
  if (shouldAutoReload) {
    return {
      message: 'Payment method updated — reloading to confirm again. No charge was made.',
      description: undefined,
    };
  }
  return {
    message: "We're having trouble loading the right payment option for this booking.",
    description: 'Please refresh the page. If the problem persists, contact us at defera.app/contact.',
  };
}
